import Link from 'next/link'
import { FunctionComponent, type MouseEventHandler } from 'react'
import styled from 'styled-components'
import { useFetchUserStatusQuery } from '@/api/user-api'
import routes from '@/hooks/use-authorization/routes'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  Footprint,
  FootprintResourceStatus,
  FootprintResourceType,
  MetaStatus,
} from '@/types/schema/directory/kol-management'

const metaStatusI18nMap: Record<MetaStatus, I18nId> = {
  unpaid: 'campaign:payment_status_unpaid',
  post_checking: 'campaign:payment_status_post_checking',
  post_checked: 'campaign:payment_status_post_checked',
  finance_checking: 'campaign:payment_status_finance_checking',
  approve: 'campaign:payment_status_approve',
  holding: 'campaign:payment_status_holding',
  reject: 'campaign:payment_status_reject',
  refunded: 'campaign:payment_status_refunded',
  paid: 'campaign:payment_status_paid',
  pending: 'campaign:internal_payment_status_pending',
  processing: 'campaign:payment_status_processing',
  'not-need': 'campaign:payment_status_no_need',
  to_be_confirmed: 'general:willingness_to_cooperate_pending',
  willing: 'general:willingness_to_cooperate_yes',
  unwilling: 'general:willingness_to_cooperate_no',
}

const resourceStatusTypeI18nMap: Record<
  FootprintResourceStatus,
  Partial<Record<FootprintResourceType, I18nId>>
> = {
  new: {
    'asset-hub': 'footprint:directory_new',
    'collection-folder': 'footprint:collection_folder_new',
    campaign: 'footprint:campaign_new',
    'deep-report': 'footprint:deep_report_new',
    competitor: 'footprint:competitor_new',
    'collaboration-detail-basic-info':
      'footprint:collaboration_detail_basic_info_new',
    'collaboration-detail-contact-info':
      'footprint:collaboration_detail_contact_info_new',
    'collaboration-detail-collaboration-record-scoring':
      'footprint:collaboration_detail_collaboration_record_scoring_new',
    'collaboration-detail-collaboration-note':
      'footprint:collaboration_detail_collaboration_note_new',
    'collaboration-detail-quotation':
      'footprint:collaboration_detail_quotation_new',
    'predictive-report-collaborative-item':
      'footprint:predictive_report_collaborative_item_upsert',
    'predictive-report': 'footprint:predictive_report_add',
  },
  remove: {
    'asset-hub': 'footprint:directory_remove',
    'collection-folder': 'footprint:collection_folder_remove',
    campaign: 'footprint:campaign_remove',
    'deep-report': 'footprint:deep_report_remove',
    competitor: 'footprint:competitor_remove',
    'collaboration-detail-basic-info':
      'footprint:collaboration_detail_basic_info_remove',
    'collaboration-detail-contact-info':
      'footprint:collaboration_detail_contact_info_remove',
    'collaboration-detail-collaboration-record-scoring':
      'footprint:collaboration_detail_collaboration_record_scoring_remove',
    'collaboration-detail-collaboration-note':
      'footprint:collaboration_detail_collaboration_note_remove',
    'collaboration-detail-quotation':
      'footprint:collaboration_detail_quotation_remove',
    'predictive-report': 'footprint:predictive_report_remove',
  },
  amend: {
    'collaboration-detail-basic-info':
      'footprint:collaboration_detail_basic_info_edit',
    'collaboration-detail-contact-info':
      'footprint:collaboration_detail_contact_info_edit',
    'collaboration-detail-collaboration-record-scoring':
      'footprint:collaboration_detail_collaboration_record_scoring_edit',
    'collaboration-detail-collaboration-note':
      'footprint:collaboration_detail_collaboration_note_edit',
    'collaboration-detail-quotation':
      'footprint:collaboration_detail_quotation_edit',
    'predictive-report-collaborative-item':
      'footprint:predictive_report_collaborative_item_upsert',
    'predictive-report-willingness-to-collaborate':
      'footprint:predictive_report_willingness_to_collaborate_edit',
  },
  unlocked: {
    unlock: 'footprint:unlocked_unlock',
  },
  update: {
    campaign: 'footprint:campaign_update',
  },
  'in-process': {
    campaign: 'footprint:campaign_in_process',
  },
  'cancel-collaboration': {
    campaign: 'footprint:campaign_cancel_collaboration',
  },
  ended: {
    campaign: 'footprint:campaign_ended',
  },
}

interface FootprintHistoryContentProps extends Footprint {
  kolName?: string
  className?: string
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>
}

const FootprintHistoryContent: FunctionComponent<
  FootprintHistoryContentProps
> = ({
  footprint_resource_type,
  footprint_resource_status,
  footprint_resource_name,
  footprint_resource_id,
  meta_status,
  kolName,
  className,
  onLinkClick,
}) => {
  const { data: userStatus } = useFetchUserStatusQuery()
  const { formatMessage } = useIntl()

  const metaStatus = meta_status
    ? formatMessage({ id: metaStatusI18nMap[meta_status] })
    : undefined

  const resourceUrl = ((): string | undefined => {
    if (!footprint_resource_name || !footprint_resource_id) {
      return undefined
    }
    switch (footprint_resource_type) {
      case 'collection-folder':
        return routes.collectionsKolId.stringifyUrl({
          id: footprint_resource_id,
        })
      case 'campaign':
        return routes.campaignListDetail.stringifyUrl(
          {},
          {
            campaignId: footprint_resource_id,
          },
        )
      case 'deep-report': {
        const workspaceId = userStatus?.currentWorkspaceId
        return workspaceId
          ? routes.reportId.stringifyUrl({
              workspaceId,
              reportId: footprint_resource_id,
            })
          : undefined
      }
      case 'competitor':
        return routes.competitiveBrandComprehensiveAnalyses.stringifyUrl({
          id: footprint_resource_id,
        })
      case 'predictive-report-collaborative-item':
      case 'predictive-report-willingness-to-collaborate':
      case 'predictive-report':
        return routes.predictiveReportIdList.stringifyUrl({
          id: footprint_resource_id,
        })
      default:
        return undefined
    }
  })()

  const contentI18nId =
    resourceStatusTypeI18nMap[footprint_resource_status][
      footprint_resource_type
    ]

  return (
    contentI18nId && (
      <Wrapper className={className}>
        {formatMessage(
          { id: contentI18nId },
          {
            resourceName: resourceUrl ? (
              <Link href={resourceUrl} onClick={onLinkClick}>
                {footprint_resource_name}
              </Link>
            ) : undefined,
            metaStatus,
            kolName,
          },
        )}
      </Wrapper>
    )
  )
}

export default styled(FootprintHistoryContent)``

const Wrapper = styled.div`
  a {
    color: ${({ theme }): string => theme.colors.brand.secondary};
    text-decoration: underline;
  }
`
